import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Layers, Satellite, ShieldCheck, Settings2, RadioTower, SlidersHorizontal,
  Gauge, ListOrdered, Wifi, Cpu, Truck, HardHat, Building2, CreditCard, RefreshCcw,
  Server, SmartphoneNfc, Check, X, Quote, Tag,
} from "lucide-react";
import SEO from "@/components/SEO";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const IMPORT_TAGS = [
  "Not consumer eSIM scanning",
  "Enterprise Remote SIM Provisioning (RSP)",
  "Multi-IMSI integration",
  "OTA provisioning",
];

const IMPORT_FLOW = [
  { icon: <Tag size={20} />, title: "Your Carrier Profile", caption: "SM-DP+ activation code" },
  { icon: <Server size={20} />, title: "PTT NZ Remote SIM Provisioning (RSP)", caption: "External IMSI imported" },
  { icon: <Layers size={20} />, title: "Multi-IMSI SIM", caption: "PTT NZ steering logic applied" },
  { icon: <SmartphoneNfc size={20} />, title: "Device", caption: "Tri-network NZ + your carrier" },
];

const COMPARISON_ROWS = [
  { feature: "Multi-network NZ", mvno: false, pttnz: "Spark + One NZ + 2degrees" },
  { feature: "External eSIM import", mvno: false, pttnz: "SM-DP+ activation supported" },
  { feature: "Multi-IMSI SIM", mvno: false, pttnz: true },
  { feature: "OTA profile injection", mvno: false, pttnz: true },
  { feature: "BYOC (actual carrier profile)", mvno: false, pttnz: true },
];

const BYOC_FAQS = [
  { q: "Can I bring my own eSIM profile?", a: "Yes — provide your carrier's SM-DP+ activation code and we'll load your profile onto our multi-IMSI SIM over the air (OTA)." },
  { q: "Does this replace your SIM?", a: "No. Your imported profile becomes an additional IMSI inside our multi-IMSI SIM — it sits alongside PTT NZ's own carrier profiles, not instead of them." },
  { q: "Does my device need to scan a QR code?", a: "No. Provisioning is done centrally over the air (OTA) through our custom-built Remote SIM Provisioning (RSP) platform — there's no on-device QR scanning step." },
  { q: "Does this work with IoT devices?", a: "Yes. It works across mobile phones (Apple and Android), routers, trackers, cameras, sensors and embedded IoT modules." },
  { q: "Does this work with a normal mobile phone?", a: "Yes. A mobile phone — whether Android, Apple (iPhone), or any other brand — will work as long as it can hold our physical SIM card." },
];

const HOW_IT_WORKS = [
  { icon: <Layers size={22} />, title: "One smart SIM, multiple profiles", text: "Your existing carrier profile (BYOC) lives on the same PTT NZ Smart SIM as our own multi-network carrier profiles — no second SIM slot, no swap." },
  { icon: <ListOrdered size={22} />, title: "Your profile stays active by default", text: "The Smart SIM runs your carrier plan first, exactly as it does today, keeping your existing plan structure, pricing and any special services intact." },
  { icon: <RadioTower size={22} />, title: "Automatic failover on data loss", text: "If your profile drops data — outage, congestion or a coverage gap — the SIM automatically switches to a PTT NZ carrier profile to keep the device connected, then can return to your profile once it's healthy again." },
  { icon: <Settings2 size={22} />, title: "Configured over the air", text: "Every setting — default profile, failover order, switching rules — is pushed remotely from the Connectivity Portal. No SIM swap, no on-device settings, no site visit." },
];

const RULE_MODES = [
  { icon: <ListOrdered size={20} />, title: "Defined priority order", text: "Set a strict fallback sequence — your profile first, then PTT NZ Carrier A, then Carrier B, then Carrier C — switching down the list only when the active profile loses data." },
  { icon: <Gauge size={20} />, title: "Signal strength & quality", text: "Instead of a fixed order, let the SIM continuously evaluate signal strength and data quality across all loaded profiles and lock to whichever is strongest at that moment." },
];

const USE_CASES = [
  { icon: <RefreshCcw size={20} />, title: "Zero-downtime fleet migration", text: "Roll new Smart SIMs out to every device now while staying live on your old carrier profile — then switch the whole fleet to a new plan over the air, whenever you're ready, without a second site visit." },
  { icon: <Truck size={20} />, title: "Existing fleet contracts", text: "Keep your negotiated carrier plan and billing exactly as-is, while gaining a mission-critical safety net if that carrier ever drops out in the field." },
  { icon: <Satellite size={20} />, title: "Starlink Direct to Cell", text: "Run Starlink Direct to Cell or another specialised service as your primary profile, with PTT NZ multi-network carriers as the automatic fallback in areas it doesn't yet reach." },
  { icon: <HardHat size={20} />, title: "Multi-carrier field teams", text: "Different depots or vehicles already locked into different carrier plans can all standardise on one Smart SIM policy, instead of juggling incompatible hardware." },
  { icon: <Building2 size={20} />, title: "Enterprise carrier agreements", text: "Preserve an existing enterprise carrier agreement for cost or compliance reasons, without giving up multi-network resilience." },
];

const PORTAL_CONTROLS = [
  "Load or replace a customer (BYOC) profile on any Smart SIM",
  "Set which profile is active / default at any time",
  "Choose fixed priority order or signal-based auto-selection",
  "Adjust failover and return-to-primary rules per SIM or per fleet",
  "Push changes instantly — no SIM swap, no device settings",
];

const BYOC = () => (
  <div data-testid="byoc-page">
    <SEO
      path="/solutions/byoc"
      title="BYOC — Bring Your Own Carrier Profile (eSIM Import)"
      description="Import your carrier's eSIM profile into a PTT NZ multi-IMSI SIM via Remote SIM Provisioning — no QR scanning, automatic multi-network failover."
      keywords="bring your own carrier SIM, BYOC SIM New Zealand, external eSIM profile import, SM-DP+ activation code, remote SIM provisioning RSP, multi-IMSI SIM New Zealand, eSIM profile failover, carrier failover SIM business, Starlink Direct to Cell failover, OTA eSIM provisioning NZ, multi-IMSI SIM NZ AU"
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Bring Your Own Carrier Profile (BYOC) — External eSIM Import",
          provider: { "@type": "Organization", name: "PTT NZ Limited" },
          areaServed: ["New Zealand", "Australia"],
          description: "PTT NZ imports a customer's existing carrier eSIM profile (via SM-DP+ activation code) directly into a multi-IMSI SIM or eSIM using enterprise Remote SIM Provisioning (RSP), with automatic over-the-air failover to PTT NZ's own multi-network carrier profiles if the primary profile loses data.",
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: BYOC_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      ]}
    />

    {/* HERO */}
    <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
        <div className="eyebrow">Bring Your Own Carrier Profile (BYOC)</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
          Keep your carrier. <span className="text-[var(--ptt-teal-deep)]">Add a network that never lets you go dark.</span>
        </h1>
        <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl leading-relaxed">
          We can import external eSIM profiles (SM-DP+ activation codes) directly into our multi-IMSI SIM/eSIM. Your carrier becomes part of our failover stack — running first, exactly as it does today, with PTT NZ's multi-network carriers stepping in automatically the moment it loses data, all managed over the air from the Connectivity Portal.
        </p>
        <div className="mt-7 flex gap-3 flex-wrap">
          <Link to="/contact?interest=BYOC%20Smart%20SIM" data-testid="byoc-hero-cta-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Talk to us about BYOC <ArrowRight size={14} /></Link>
        </div>
      </div>
    </section>

    {/* EXTERNAL ESIM PROFILE IMPORT */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">The differentiator</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">External eSIM Profile Import.</h2>
      <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl leading-relaxed">
        We support carrier-grade eSIM profile import using your SM-DP+ activation code. Your profile is downloaded OTA and embedded into our multi-IMSI SIM. Your carrier becomes part of our steering logic and redundancy model — no QR scanning on devices, provisioning is done centrally through our custom-built Remote SIM Provisioning (RSP).
      </p>
      <div className="flex flex-wrap gap-2 mt-6" data-testid="byoc-import-tags">
        {IMPORT_TAGS.map((t) => (
          <span key={t} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5"><Check size={11} className="text-[var(--ptt-teal-deep)]" />{t}</span>
        ))}
      </div>

      {/* IMPORT FLOW DIAGRAM */}
      <div className="mt-10 border border-[var(--ptt-line)] bg-white p-6 lg:p-8" data-testid="byoc-import-flow-diagram">
        <div className="flex flex-col lg:flex-row items-stretch gap-3">
          {IMPORT_FLOW.map((step, i) => (
            <React.Fragment key={step.title}>
              <div className="border border-[var(--ptt-line)] p-5 flex-1">
                <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15">{step.icon}</div>
                <div className="font-display text-sm font-bold mt-4 leading-snug">{step.title}</div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal-deep)] mt-2">{step.caption}</div>
              </div>
              {i < IMPORT_FLOW.length - 1 && (
                <div className="flex lg:flex-col items-center justify-center text-[var(--ptt-muted)] shrink-0">
                  <ArrowRight size={20} className="hidden lg:block" />
                  <ArrowRight size={20} className="lg:hidden rotate-90" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* REAL-WORLD EXAMPLE */}
      <div className="mt-8 border border-[var(--ptt-ink)] bg-[var(--ptt-paper)] p-6 lg:p-8 flex gap-4" data-testid="byoc-real-world-example">
        <Quote size={22} className="text-[var(--ptt-teal-deep)] shrink-0 mt-1" />
        <p className="text-[var(--ptt-ink-soft)] leading-relaxed">
          If you have an existing Telstra, Spark, or international IoT eSIM profile, we can import it using your activation code and merge it into our multi-IMSI SIM. Your devices gain your carrier and your current carrier pricing, plus tri-network NZ redundancy.
        </p>
      </div>
    </section>

    {/* COMPARISON TABLE */}
    <section className="bg-[var(--ptt-paper)] border-y border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow">Why it's different</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Not your typical MVNO.</h2>
        <div className="mt-10 overflow-x-auto border border-[var(--ptt-line)] bg-white" data-testid="byoc-comparison-table">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="border-b border-[var(--ptt-line)]">
                <th className="text-left font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] p-4">Feature</th>
                <th className="text-left font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] p-4">Typical MVNO</th>
                <th className="text-left font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal-deep)] p-4">PTT NZ</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((r) => (
                <tr key={r.feature} className="border-b border-[var(--ptt-line)] last:border-b-0" data-testid={`byoc-compare-row-${r.feature.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '')}`}>
                  <td className="p-4 font-semibold">{r.feature}</td>
                  <td className="p-4"><X size={16} className="text-[var(--ptt-muted)]" /></td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 text-[var(--ptt-ink-soft)]">
                      <Check size={16} className="text-[var(--ptt-teal-deep)] shrink-0" />
                      {typeof r.pttnz === "string" && <span>{r.pttnz}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* HOW IT WORKS */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">How it works</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Your plan stays the default. Ours is the safety net.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="byoc-how-it-works-grid">
        {HOW_IT_WORKS.map((h) => (
          <div key={h.title} className="bg-white border border-[var(--ptt-line)] p-6 hover:border-[var(--ptt-ink)] transition-colors" data-testid={`byoc-how-${h.title.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{h.icon}</div>
            <h3 className="font-display text-base font-bold mt-5">{h.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{h.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* DIAGRAM */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
      <div className="eyebrow">Profile priority</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">One Smart SIM, 8+ profiles deep.</h2>
      <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl leading-relaxed">
        Your BYOC profile sits at the top and stays active by default. Beneath it, PTT NZ loads multiple carrier profiles as fallbacks — Carrier A, then Carrier B, then Carrier C, and so on — with capacity for 8 or more profiles on a single card, so the device always has somewhere left to go if one drops out.
      </p>

      <div className="mt-10 border border-[var(--ptt-line)] bg-white p-4 lg:p-6" data-testid="byoc-priority-diagram">
        <img
          src="/assets/byoc-priority-diagram.webp"
          alt="Diagram showing a Smart SIM with the customer's own carrier profile active by default at the top, stacked above several PTT NZ carrier fallback profiles that failover in order with more profile slots continuing below, and automatic network handover shown by a signal cloud icon"
          className="w-full h-auto"
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6 pt-6 border-t border-[var(--ptt-line)]">
          <div className="flex items-center gap-2.5">
            <span className="font-mono-tech text-[10px] text-[var(--ptt-teal-deep)] shrink-0">01</span>
            <div>
              <div className="font-display text-xs font-bold">Your profile (BYOC)</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Active by default</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="font-mono-tech text-[10px] text-[var(--ptt-muted)] shrink-0">02</span>
            <div>
              <div className="font-display text-xs font-bold">PTT NZ Carrier A</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">First fallback</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="font-mono-tech text-[10px] text-[var(--ptt-muted)] shrink-0">03</span>
            <div>
              <div className="font-display text-xs font-bold">PTT NZ Carrier B</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Second fallback</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="font-mono-tech text-[10px] text-[var(--ptt-muted)] shrink-0">04</span>
            <div>
              <div className="font-display text-xs font-bold">PTT NZ Carrier C</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Third fallback</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="font-mono-tech text-[10px] text-[var(--ptt-muted)] shrink-0">05+</span>
            <div>
              <div className="font-display text-xs font-bold">More profiles</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Up to 8+ total capacity</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* RULE MODES */}
    <section className="bg-[var(--ptt-ink)] text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow text-white/50">Failover logic</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Choose how the switch is decided.</h2>
        <div className="grid sm:grid-cols-2 gap-5 mt-10" data-testid="byoc-rule-modes-grid">
          {RULE_MODES.map((r) => (
            <div key={r.title} className="border border-white/15 p-7" data-testid={`byoc-rule-${r.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="w-11 h-11 grid place-items-center bg-white/10 text-[var(--ptt-teal)]">{r.icon}</div>
              <h3 className="font-display text-lg font-bold mt-5">{r.title}</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CONNECTIVITY PORTAL CONTROLS */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-b border-[var(--ptt-line)]">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow">Connectivity Portal</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">
            Change anything, <span className="text-[var(--ptt-teal-deep)]">from anywhere.</span>
          </h2>
          <p className="mt-5 text-[var(--ptt-ink-soft)] leading-relaxed max-w-md">
            Every part of a BYOC configuration is managed over the air — no physical SIM changes and no touching device settings. Adjust priorities, switch the default profile, or change the failover logic for a single SIM or a whole fleet in seconds.
          </p>
          <div className="mt-7">
            <a href="https://portal.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="byoc-portal-cta" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Open Connectivity Portal <ArrowRight size={14} /></a>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="border border-[var(--ptt-ink)] bg-white p-6 lg:p-8">
            <div className="flex items-center gap-2.5 mb-5">
              <SlidersHorizontal size={16} className="text-[var(--ptt-teal-deep)]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Configured entirely over the air</span>
            </div>
            <ul className="space-y-3">
              {PORTAL_CONTROLS.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm border-b border-[var(--ptt-line)] last:border-b-0 pb-3 last:pb-0">
                  <ShieldCheck size={15} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />
                  <span className="text-[var(--ptt-ink-soft)]">{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-dashed border-[var(--ptt-line)] font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">
              Every physical SIM PTT NZ sells is already a Smart SIM — no special order needed, just a profile set up and pushed over the air
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* USE CASES */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">Where BYOC fits</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Keep what works. Insure against what doesn't.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="byoc-use-cases-grid">
        {USE_CASES.map((u) => (
          <div key={u.title} className="bg-white border border-[var(--ptt-line)] p-6 hover:border-[var(--ptt-ink)] transition-colors" data-testid={`byoc-use-case-${u.title.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15">{u.icon}</div>
            <h3 className="font-display text-base font-bold mt-4">{u.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{u.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* FAQ */}
    <section className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="byoc-faq-section">
      <div className="eyebrow">FAQ</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Bring Your Own Carrier, answered.</h2>
      <Accordion type="single" collapsible className="mt-8" data-testid="byoc-faq-accordion">
        {BYOC_FAQS.map((f, i) => (
          <AccordionItem key={f.q} value={`byoc-faq-${i}`} className="border-[var(--ptt-line)]" data-testid={`byoc-faq-item-${i}`}>
            <AccordionTrigger data-testid={`byoc-faq-trigger-${i}`} className="font-display text-base font-bold text-left hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent data-testid={`byoc-faq-content-${i}`} className="text-[var(--ptt-muted)] leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>

    {/* FINAL CTA */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 mb-8">
      <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Talk to us</div>
            <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Bring your carrier. We'll bring the backup.</h3>
            <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">Tell us which carrier plan you want to keep and how you want failover to behave — we'll set up the Smart SIM and priority rules around it.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/contact?interest=BYOC%20Smart%20SIM" data-testid="byoc-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Talk to us about BYOC <ArrowRight size={16} /></Link>
            <Link to="/solutions" data-testid="byoc-final-cta-solutions" className="ghost-button px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Back to Solutions</Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default BYOC;
